'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';

export default function QualityOverMomentumPage() {
  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--background)', color: 'var(--foreground)' }}
    >
      <header
        className="sticky top-0 z-50 flex items-center justify-between px-6 md:px-12 lg:px-20"
        style={{
          height: '72px',
          backgroundColor: 'rgba(13,26,58,0.95)',
          borderBottom: '1px solid rgba(30,48,104,0.6)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <Link
          href="/#insights"
          className="flex items-center gap-2 text-sm font-medium"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <ArrowLeft size={15} />
          Back to Perspectives
        </Link>
        <div className="flex items-center gap-2">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/assets/macro-capital-logo.svg" alt="Macro Capital" style={{ width: '36px', height: '36px' }} />
          <span className="text-sm font-bold tracking-widest uppercase" style={{ color: 'var(--foreground)' }}>
            Macro Capital
          </span>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <span
            className="text-[10px] font-semibold tracking-[0.18em] uppercase px-3 py-1 rounded-full"
            style={{
              backgroundColor: 'rgba(26,46,107,0.15)',
              border: '1px solid rgba(26,46,107,0.3)',
              color: '#6b8cda',
            }}
          >
            Portfolio
          </span>
          <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Calendar size={11} /> March 2025
          </span>
          <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Clock size={11} /> 6 min read
          </span>
        </div>

        <h1
          className="font-semibold leading-tight mb-6"
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
            color: 'var(--foreground)',
          }}
        >
          Quality Over Momentum in Uncertain Markets
        </h1>

        <p className="text-lg leading-relaxed mb-10" style={{ color: 'var(--muted-foreground)' }}>
          When macro visibility is low, the premium on business quality rises. We explore what that means for portfolio construction — and why the distinction between quality and momentum matters more than ever in the current environment.
        </p>

        <div className="h-px mb-10" style={{ backgroundColor: 'var(--border)' }} />

        <div className="space-y-8" style={{ color: 'var(--muted-foreground)', lineHeight: '1.85' }}>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Defining Quality in Investment Terms
            </h2>
            <p className="mb-4">
              The word "quality" is used liberally in investment circles, often as a synonym for "good" without much analytical rigour. For our purposes, quality has a precise meaning: it refers to businesses that generate high returns on invested capital, sustain those returns over time, and do so with limited reliance on leverage or external financing.
            </p>
            <p className="mb-4">
              The empirical evidence for quality as an investment factor is robust. Academic research spanning multiple decades and geographies consistently shows that high-quality businesses — measured by return on equity, earnings stability, and balance sheet strength — outperform lower-quality peers over full market cycles. The outperformance is not dramatic in any single year, but it compounds powerfully over time.
            </p>
            <p>
              Momentum, by contrast, is a factor that captures recent price performance. Stocks that have risen tend to continue rising, at least over short to medium horizons. The momentum factor has also been well-documented academically, but it is fundamentally different from quality: it is a statistical regularity, not a reflection of underlying business economics. And it is far more fragile — momentum strategies can suffer severe drawdowns when market regimes shift.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Why Uncertainty Amplifies the Quality Premium
            </h2>
            <p className="mb-4">
              In periods of low macro uncertainty — when growth is steady, inflation is contained, and central bank policy is predictable — the quality premium tends to compress. Investors are willing to pay up for growth and momentum because the macro backdrop provides a stable foundation. The rising tide lifts all boats, and the distinction between high-quality and lower-quality businesses matters less.
            </p>
            <p className="mb-4">
              But when macro uncertainty rises — when the growth outlook is unclear, when inflation is volatile, when central bank policy is in flux — the quality premium expands. Investors become more discriminating. They seek businesses that can sustain earnings through the cycle, that have pricing power to offset cost inflation, and that have balance sheets strong enough to weather a credit tightening. These are, by definition, quality businesses.
            </p>
            <p>
              The current environment — characterised by moderating but still-elevated inflation, uncertain growth dynamics, and a central bank navigating a delicate easing cycle — is precisely the kind of environment where quality tends to outperform. The macro backdrop is constructive but not certain, and that uncertainty creates a premium for businesses that can perform regardless of the macro outcome.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              The Danger of Momentum in Regime Shifts
            </h2>
            <p className="mb-4">
              Momentum strategies are particularly vulnerable at regime inflection points — moments when the macro environment shifts from one state to another. The 2022 rate shock is a recent and instructive example. The highest-momentum stocks of 2020 and 2021 — many of them high-growth technology companies with minimal current earnings — suffered catastrophic drawdowns as rates rose and the discount rate applied to future cash flows increased sharply.
            </p>
            <p className="mb-4">
              The problem with momentum is that it is inherently backward-looking. It tells you what has worked recently, not what will work going forward. In a stable regime, this is a reasonable heuristic — what worked recently is likely to continue working. But at regime inflection points, the heuristic breaks down precisely when it matters most.
            </p>
            <p>
              Quality, by contrast, is forward-looking in the sense that it reflects the underlying economics of the business. A company with high returns on capital, strong competitive advantages, and a clean balance sheet will likely continue to generate attractive returns regardless of the macro regime. It may underperform in a momentum-driven bull market, but it will preserve capital in a downturn and compound steadily over the full cycle.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Portfolio Construction Implications
            </h2>
            <p className="mb-4">
              For portfolio construction, the quality-over-momentum framework has several practical implications. First, it argues for concentration over diversification. A portfolio of twenty to thirty high-quality businesses, held with conviction, will typically outperform a broadly diversified portfolio that includes lower-quality names. The diversification benefit of adding lower-quality stocks is outweighed by the drag they impose on overall portfolio quality.
            </p>
            <p className="mb-4">
              Second, it argues for patience. Quality businesses are rarely cheap. They trade at premium valuations because the market recognises their superior economics. The temptation is to rotate into cheaper, lower-quality names when valuations look stretched. This temptation should generally be resisted — the valuation premium on quality is usually justified, and the apparent cheapness of lower-quality businesses often reflects genuine fundamental weakness.
            </p>
            <p>
              Third, it argues for a long holding period. The compounding effect of quality — the reinvestment of high returns on capital at similarly high rates — takes time to manifest. Investors who trade in and out of quality businesses, chasing short-term momentum, systematically underperform those who hold with conviction through the inevitable periods of underperformance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Our Current Positioning
            </h2>
            <p className="mb-4">
              Our portfolio reflects a strong quality bias. We hold businesses with average return on equity above 20%, net debt-to-EBITDA below 1.5x, and earnings that have grown through at least two prior recessions. These are not exciting businesses in the momentum sense — they do not appear on lists of the year's best performers. But they are businesses that we expect to be worth significantly more in five to ten years than they are today.
            </p>
            <p>
              In the current environment of elevated uncertainty, we believe this quality bias is not merely a stylistic preference but a fundamental risk management tool. It reduces the portfolio's sensitivity to macro outcomes, provides a margin of safety against valuation compression, and ensures that the businesses we own can sustain and grow their earnings regardless of the path of interest rates, inflation, or economic growth.
            </p>
          </section>

        </div>

        <div className="mt-16 pt-8" style={{ borderTop: '1px solid var(--border)' }}>
          <p className="text-xs mb-6" style={{ color: 'var(--muted-foreground)' }}>
            This article is for informational purposes only and does not constitute investment advice. Past performance is not indicative of future results. For qualified investors only.
          </p>
          <Link
            href="/#insights"
            className="inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: 'var(--primary)' }}
          >
            <ArrowLeft size={14} />
            Back to all perspectives
          </Link>
        </div>
      </article>
    </div>
  );
}
